import { useId, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Add from '@mui/icons-material/Add';
import { Button } from '@/ui';
import { CriteriaPaneProps, InternalPrePing, SourceType } from '../types';
import {
  CriteriaSection,
  EmptyHint,
  EntryCard,
  FieldGrid,
  FieldLabel,
  FullRow,
  Input,
  MultiSelect,
  SelectSimple,
  Switch,
  TextArea,
} from '../FormComponents';
import { useHandlerOptions, useOfferActionOptions, useOfferOptions } from '../useCriteriaOptions';
import { AND_OR_OPTIONS } from '../constants';
import { HELP } from '../criteriaHelp';
import { isRevenueSuppressionLevel, maxLimitPlaceholder, maxLimitTooltip } from '../suppressionCopy';
import { hasValue } from './shared';
import type { CriteriaSectionDef } from './types';
import TestPostButton from '../../../../views/Offers/OfferActions/TestVendorRequest/TestVendorRequestButton';

const SUPPRESSION_TYPES = [
  { label: 'Days', value: 'days' },
  { label: 'Hours', value: 'hours' },
  { label: 'Minutes', value: 'minutes' },
];

const SUPPRESSION_ANSWERS = [
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' },
  { label: 'Maybe', value: 'maybe' },
  { label: 'Not Sure', value: 'not_sure' },
];

const PRE_PING_METHODS = ['GET', 'POST', 'JSON', 'XML'].map((m) => ({ label: m, value: m }));
const METHODS_WITH_BODY = new Set(['POST', 'JSON', 'XML']);

const isOfferActionSource = (sourceType: SourceType) =>
  sourceType === 'offerAction' || sourceType === 'offerActionLinkout';

/** What a suppression counts depends on what the criteria are attached to. */
function suppressionLevels(sourceType: SourceType) {
  if (sourceType === 'offerActionLinkout') {
    return [
      { label: 'Action Taken', value: 'action_taken' },
      { label: 'Revenue Generated', value: 'revenue_generated' },
      { label: 'Email Redirect', value: 'email_redirect' },
      { label: 'Email Redirect Rev Generated', value: 'email_redirect_rev_generated' },
    ];
  }
  if (sourceType === 'offerAction') {
    return [
      { label: 'Action Taken', value: 'action_taken' },
      { label: 'Revenue Generated', value: 'revenue_generated' },
    ];
  }
  return [
    { label: 'Calculated Revenue', value: 'calculated_revenue' },
    { label: 'Accepted', value: 'accepted' },
    { label: 'Impression', value: 'impression' },
    { label: 'Answer', value: 'answer' },
  ];
}

const VALID_SUPPRESSION_TYPES = new Set(['days', 'hours', 'minutes']);
const isCompleteSuppression = (ping: InternalPrePing) =>
  VALID_SUPPRESSION_TYPES.has(String(ping?.prePingSuppressionType ?? '').toLowerCase());

/* ----------------------------------------------------------------------- Internal Pre-Pings */

function InternalPrePingsSection({ state, setState, sourceType, errors }: CriteriaPaneProps) {
  const isPublisher = sourceType === 'publisherPrePingCriteria';
  const forActions = isOfferActionSource(sourceType);
  const offers = useOfferOptions();
  const offerActions = useOfferActionOptions();
  const entries = state.internalPrePings?.internalPrePings || [];

  const entryErrors = (errors?.internalPrePingConfiguration as Record<string, unknown> | undefined)?.internalPrePings;
  const errorFor = (idx: number, field: keyof InternalPrePing) => {
    const entry = Array.isArray(entryErrors) ? (entryErrors[idx] as Record<string, unknown> | undefined) : undefined;
    const message = entry?.[field];
    return typeof message === 'string' ? message : undefined;
  };

  const setEntries = (update: (list: InternalPrePing[]) => InternalPrePing[]) =>
    setState((s) => ({
      ...s,
      internalPrePings: {
        andOrLogic: s.internalPrePings?.andOrLogic || 'AND',
        internalPrePings: update(s.internalPrePings?.internalPrePings || []),
      },
    }));
  const updateEntry = (idx: number, patch: Partial<InternalPrePing>) =>
    setEntries((list) => list.map((e, i) => (i === idx ? { ...e, ...patch } : e)));

  return (
    <CriteriaSection
      title='Internal Pre-Pings'
      help={HELP.internalPrePings}
      actions={
        <Button
          color='primary'
          startIcon={<Add />}
          onClick={() => setEntries((list) => [...list, { prePingSuppressionType: 'none' }])}
        >
          Add Suppression
        </Button>
      }
    >
      <FieldGrid min='20rem'>
        <SelectSimple
          label='AND/OR logic'
          value={state.internalPrePings?.andOrLogic || ''}
          onChange={(v) =>
            setState((s) => ({
              ...s,
              internalPrePings: {
                andOrLogic: v || 'AND',
                internalPrePings: s.internalPrePings?.internalPrePings || [],
              },
            }))
          }
          options={AND_OR_OPTIONS}
          placeholder='Select Logic'
          help={HELP.andOrLogic}
        />
      </FieldGrid>

      {entries.length === 0 && <EmptyHint>No suppressions. Use Add Suppression to create one.</EmptyHint>}

      {entries.map((item, idx) => {
        const hasType = Boolean(item.prePingSuppressionType) && item.prePingSuppressionType !== 'none';
        return (
          <EntryCard
            key={idx}
            title={`Suppression ${idx + 1}`}
            onRemove={() => setEntries((list) => list.filter((_, j) => j !== idx))}
            removeLabel='Remove this suppression'
          >
            <FieldGrid min='11rem'>
              <SelectSimple
                label='Type'
                value={item.prePingSuppressionType || ''}
                onChange={(v) => updateEntry(idx, { prePingSuppressionType: v })}
                options={SUPPRESSION_TYPES}
                error={errorFor(idx, 'prePingSuppressionType')}
                help={HELP.suppressionType}
              />
              {hasType && (
                <Input
                  label='Amount'
                  type='number'
                  value={item.prePingSuppressionAmount?.toString() ?? ''}
                  onChange={(v) =>
                    setEntries((list) =>
                      list.map((e, i) => {
                        if (i !== idx) return e;
                        const { prePingSuppressionAmount: _drop, ...rest } = e;
                        return v === '' ? rest : { ...e, prePingSuppressionAmount: Number(v) };
                      })
                    )
                  }
                  error={errorFor(idx, 'prePingSuppressionAmount')}
                  help={HELP.suppressionAmount}
                />
              )}
              <SelectSimple
                label='Level'
                value={item.prePingSuppressionLevel || ''}
                onChange={(v) => updateEntry(idx, { prePingSuppressionLevel: v })}
                options={suppressionLevels(sourceType)}
                error={errorFor(idx, 'prePingSuppressionLevel')}
                help={HELP.suppressionLevel}
              />
              {item.prePingSuppressionLevel === 'answer' && (
                <SelectSimple
                  label='Answer'
                  value={item.prePingSuppressionAnswer ?? ''}
                  onChange={(v) => updateEntry(idx, { prePingSuppressionAnswer: v })}
                  options={SUPPRESSION_ANSWERS}
                  placeholder='Select Answer'
                />
              )}
              <Input
                label='Max Limit'
                type='number'
                min={0}
                value={item.prePingSuppressionMaxLimit?.toString() ?? ''}
                onChange={(v) => updateEntry(idx, { prePingSuppressionMaxLimit: v === '' ? '' : Number(v) })}
                placeholder={maxLimitPlaceholder(item.prePingSuppressionLevel)}
                // A revenue level totals dollars rather than counting records, so the field says so
                // instead of leaving "3" to read as three hand-offs.
                startAdornment={isRevenueSuppressionLevel(item.prePingSuppressionLevel) ? '$' : undefined}
                help={maxLimitTooltip(item.prePingSuppressionLevel)}
              />
              {hasType && !isPublisher && (
                <FullRow>
                  <MultiSelect
                    label={forActions ? 'Additional Offer Actions' : 'Additional Offers'}
                    help={HELP.suppressionAdditional}
                    value={((forActions ? item.additionalOfferActions : item.additionalOffers) || []).map(String)}
                    onChange={(ids) =>
                      updateEntry(
                        idx,
                        forActions ? { additionalOfferActions: ids.map(Number) } : { additionalOffers: ids.map(Number) }
                      )
                    }
                    options={forActions ? offerActions.options : offers.options}
                    placeholder={`Select ${forActions ? 'Offer Actions' : 'Offers'}...`}
                    disabled={offers.isLoading || offerActions.isLoading}
                  />
                </FullRow>
              )}
            </FieldGrid>
          </EntryCard>
        );
      })}
    </CriteriaSection>
  );
}

/* ------------------------------------------------------------------------- External Pre-Ping */

function HandlerSelect({
  value,
  onChange,
}: {
  value: { id?: number | string } | null;
  onChange: (h: unknown) => void;
}) {
  const id = useId();
  const { handlers, options } = useHandlerOptions();
  return (
    <Box sx={{ minWidth: 0 }}>
      <FieldLabel htmlFor={id} help={HELP.prePingHandler}>
        Handler
      </FieldLabel>
      <Autocomplete
        id={id}
        options={options}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, selected) => option.value === selected.value}
        value={options.find((o) => o.value === value?.id?.toString()) ?? null}
        // The configuration stores the whole handler record, not just its ID.
        onChange={(_event, opt) => onChange(opt ? handlers.find((h) => String(h.id) === opt.value) || null : null)}
        renderInput={(params) => <TextField {...params} placeholder='Select handler' />}
      />
    </Box>
  );
}

function ExternalPrePingSection({ state, setState }: CriteriaPaneProps) {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const set =
    <K extends keyof typeof state>(key: K) =>
    (value: (typeof state)[K]) =>
      setState((s) => ({ ...s, [key]: value }));

  return (
    <CriteriaSection title='External Pre-Ping' help={HELP.externalPrePing}>
      <FieldGrid min='17rem'>
        <Switch
          id='sessioncap'
          label='Pre-Ping Result Stored'
          checked={state.prePingResultSessionCapture}
          onChange={set('prePingResultSessionCapture')}
          tooltip={HELP.prePingResultStored}
        />
        <Switch
          id='advancedPreping'
          label='Advanced External Preping Enabled'
          checked={state.externalPrePingProcessingEnabled}
          onChange={set('externalPrePingProcessingEnabled')}
          tooltip={HELP.advancedPrePing}
        />
      </FieldGrid>
      <FieldGrid min='14rem'>
        <SelectSimple
          label='Pre Ping Method'
          value={state.prePingMethod}
          onChange={set('prePingMethod')}
          options={PRE_PING_METHODS}
          placeholder='Select method'
          help={HELP.prePingMethod}
        />
        <HandlerSelect value={state.prePingHandlerId} onChange={set('prePingHandlerId')} />
        {!!state.prePingMethod && (
          <FullRow>
            <Input
              label='URL'
              value={state.prePingUrl}
              onChange={set('prePingUrl')}
              placeholder='https://...'
              help={HELP.prePingUrl}
            />
          </FullRow>
        )}
        {METHODS_WITH_BODY.has(state.prePingMethod) && (
          <FullRow>
            {/* Grows with its content, capped so a very long body never swallows the page. */}
            <TextArea
              label='Body'
              value={state.prePingBody}
              onChange={set('prePingBody')}
              minRows={4}
              maxRows={30}
              help={HELP.prePingBody}
            />
          </FullRow>
        )}
      </FieldGrid>

      {!!state.prePingMethod && (
        <>
          <Stack direction='row' spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
            <Button color='outline-secondary' onClick={() => setAdvancedOpen((open) => !open)}>
              {advancedOpen ? 'Hide Advanced' : 'Show Advanced'}
            </Button>
            <TestPostButton
              prefix={'prePing'}
              initialValues={{
                url: state.prePingUrl,
                body: state.prePingBody,
                method: state.prePingMethod,
                handler: state.prePingHandlerId,
                headers: [state.prePingHeader1, state.prePingHeader2, state.prePingHeader3],
                timeout: state.prePingTimeout,
              }}
            />
          </Stack>
          {advancedOpen && (
            <FieldGrid min='12rem'>
              <Input
                label='Timeout (ms)'
                type='number'
                value={state.prePingTimeout?.toString() || ''}
                onChange={(v) => {
                  const ms = v === '' ? undefined : Number(v);
                  setState((s) => ({ ...s, prePingTimeout: ms === undefined || isNaN(ms) ? undefined : ms }));
                }}
                help={HELP.prePingTimeout}
              />
              <Input
                label='Header 1'
                value={state.prePingHeader1}
                onChange={set('prePingHeader1')}
                help={HELP.prePingHeader}
              />
              <Input
                label='Header 2'
                value={state.prePingHeader2}
                onChange={set('prePingHeader2')}
                help={HELP.prePingHeader}
              />
              <Input
                label='Header 3'
                value={state.prePingHeader3}
                onChange={set('prePingHeader3')}
                help={HELP.prePingHeader}
              />
              <Input
                label='HTTP Status'
                value={state.prePingCustomHttpStatus}
                onChange={set('prePingCustomHttpStatus')}
                help={HELP.prePingHttpStatus}
              />
            </FieldGrid>
          )}
        </>
      )}
    </CriteriaSection>
  );
}

/* ---------------------------------------------------------------------------------- Registry */

export const PRE_PING_SECTIONS: CriteriaSectionDef[] = [
  {
    id: 'internalPrePings',
    tab: 'preping',
    title: 'Internal Pre-Pings',
    fields: ['AND/OR logic'],
    aliases: [
      'Type',
      'Amount',
      'Level',
      'Answer',
      'Max Limit',
      'Additional Offers',
      'Additional Offer Actions',
      'suppression',
      'suppress',
      'frequency',
      'history',
    ],
    stateKeys: ['internalPrePings'],
    isConfigured: (s) => (s.internalPrePings?.internalPrePings || []).some(isCompleteSuppression),
    Component: InternalPrePingsSection,
  },
  {
    id: 'externalPrePing',
    tab: 'preping',
    title: 'External Pre-Ping',
    fields: ['Pre-Ping Result Stored', 'Advanced External Preping Enabled', 'Pre Ping Method', 'Handler'],
    aliases: ['URL', 'Body', 'Timeout (ms)', 'Header 1', 'HTTP Status', 'external', 'api', 'post', 'test post'],
    stateKeys: [
      'prePingResultSessionCapture',
      'externalPrePingProcessingEnabled',
      'prePingMethod',
      'prePingHandlerId',
      'prePingUrl',
      'prePingBody',
      'prePingTimeout',
      'prePingHeader1',
      'prePingHeader2',
      'prePingHeader3',
      'prePingCustomHttpStatus',
    ],
    appliesTo: ({ isPublisher }) => !isPublisher,
    isConfigured: (s) =>
      s.prePingResultSessionCapture ||
      s.externalPrePingProcessingEnabled ||
      [
        s.prePingMethod,
        s.prePingHandlerId,
        s.prePingUrl,
        s.prePingBody,
        s.prePingTimeout,
        s.prePingHeader1,
        s.prePingHeader2,
        s.prePingHeader3,
        s.prePingCustomHttpStatus,
      ].some(hasValue),
    Component: ExternalPrePingSection,
  },
];
